// Copyright ©️ 2026 eVolve MEP, LLC

import { useEvolvePost, type Facility, type StrongOmit, type WorkCell } from '@evolvemep/api-ts';
import { Input, TextInput, Stack, Text, getDefaultZIndex } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { TextareaWrapped } from 'components/Mantine/TextInputWrapped';

type Props = {
  opened: boolean;
  onClose: () => void;
  defaultName?: string;
  facility: Pick<Facility, 'facilityId' | 'facilityName'>;
  onWorkCellCreated: (workCell: WorkCell) => void;
};

type FormValues = {
  workCellName: string;
  workCellDescription: string;
};

const CreateWorkCellForm = ({ defaultName, onWorkCellCreated, facility }: StrongOmit<Props, 'opened' | 'onClose'>) => {
  const { apiCall: createWorkCellApiCall } = useEvolvePost('shop/workCell');
  const { facilityId, facilityName } = facility;

  const form = useForm<FormValues>({
    initialValues: {
      workCellName: defaultName ?? '',
      workCellDescription: '',
    },
    validate: {
      workCellName: (v) => !v.trim(),
    },
  });

  const onSubmit = async (values: typeof form.values) =>
    createWorkCellApiCall({
      ...values,
      facilityId,
    }).then((workCell) => {
      onWorkCellCreated(workCell);
      notifications.show({
        title: 'Successfully created work cell',
        message: `Work cell ${workCell.workCellName} created.`,
        color: 'green',
      });
    });

  useFormModal(form, onSubmit);

  return (
    <>
      <Stack gap={0}>
        <Input.Label>Facility</Input.Label>
        <Text>{facilityName}</Text>
      </Stack>
      <TextInput
        label="Work Cell Name"
        required
        autoFocus
        data-autofocus
        maxLength={64}
        {...form.getInputProps('workCellName')}
      />
      <TextareaWrapped label="Description" maxLength={255} {...form.getInputProps('workCellDescription')} />
    </>
  );
};

export const CreateWorkCellModal = ({ opened, onClose, ...props }: Props) => (
  <FormModal
    title="Create work cell"
    opened={opened}
    onClose={onClose}
    type="submit"
    confirmationText="Create"
    startDirty={!!props.defaultName}
    // This Modal is often rendered inside of a `SetTaskWorkCellPopover`
    zIndex={getDefaultZIndex('popover') + 1}
  >
    <CreateWorkCellForm {...props} />
  </FormModal>
);
